import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import * as Sentry from '@sentry/vue'

import { authApi } from '@/api/auth'
import type { Usuario } from '@/types/api'

export const useAuthStore = defineStore('auth', () => {
  const usuario = ref<Usuario | null>(null)
  const cargando = ref(false)
  const bootstrapped = ref(false)

  const isAuthenticated = computed(() => usuario.value !== null)
  const isAdmin = computed(() => usuario.value?.rol === 'admin')

  // Solo id y rol: suficiente para agrupar errores por usuario sin exponer datos.
  watch(usuario, (u) => {
    Sentry.setUser(u ? { id: String(u.id), segment: u.rol } : null)
  })

  async function bootstrap() {
    if (bootstrapped.value) return
    cargando.value = true
    try {
      usuario.value = await authApi.me()
    } catch {
      usuario.value = null
    } finally {
      cargando.value = false
      bootstrapped.value = true
    }
  }

  async function login(username: string, password: string) {
    const { user } = await authApi.login({ username, password })
    usuario.value = user
    return user
  }

  /**
   * Cerrar sesión nunca lanza: si la petición falla (token vencido, sin red,
   * timeout), la sesión local se limpia igual y quien llama sigue su camino
   * al login. Si el refresh quedó vivo en el servidor, expira solo.
   */
  async function logout() {
    try {
      await authApi.logout()
    } catch {
      // Se ignora a propósito; ver docstring.
    } finally {
      usuario.value = null
    }
  }

  async function logoutAll() {
    try {
      await authApi.logoutAll()
    } catch {
      // Ídem.
    } finally {
      usuario.value = null
    }
  }

  function clear() {
    usuario.value = null
  }

  return {
    usuario,
    cargando,
    bootstrapped,
    isAuthenticated,
    isAdmin,
    bootstrap,
    login,
    logout,
    logoutAll,
    clear,
  }
})
